import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { TooltipVariant } from 'cpool-components/general/Tooltip/types';
import {
    CHAT_MEDIA_SIZE_LIMITS,
    CHAT_MESSAGE_TYPES,
    type TemplateVariableSource,
    USER_ROLES,
    WHATSAPP_TEMPLATE_PARAMETER_KINDS,
    WHATSAPP_TEMPLATE_VARIABLE_SOURCES,
    type WhatsAppVariableName,
} from 'cpool-constants';
import { deriveMediaTypeFromMimeType, formatWhatsAppAgentSalutation } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuMessageCircle, LuMessageSquareText, LuPaperclip, LuSend } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { ChipComposerInput } from './ChipComposerInput';
import { TemplatePickerOverlay } from './TemplatePickerOverlay';
import { useUserData } from '~/components/Header/menu/hooks/useUserData';
import useLead from '~/features/leadDetails/hooks/useLead';
import { CHAT_WINDOW_STATES, type ChatWindowState } from '~/features/whatsapp/constants/chatStatus';
import { WHATSAPP_TEMPLATE_PICKER_TABS } from '~/features/whatsapp/constants/templatePicker';
import { useChatContextLoanRequestId } from '~/features/whatsapp/hooks/useChatContextLoanRequestId';
import { useChats } from '~/features/whatsapp/hooks/useChats';
import { usePartnerContacts } from '~/features/whatsapp/hooks/usePartnerContacts';
import { useSendMessageMutation } from '~/features/whatsapp/hooks/useSendMessageMutation';
import { useSendTemplateMessageMutation } from '~/features/whatsapp/hooks/useSendTemplateMessageMutation';
import { useUploadMediaMutation } from '~/features/whatsapp/hooks/useUploadMediaMutation';
import type { WhatsAppTemplate } from '~/features/whatsapp/hooks/useWhatsAppTemplates';
import { useWhatsAppTemplates } from '~/features/whatsapp/hooks/useWhatsAppTemplates';
import { clearAutoOpenTemplatePicker, useChatPanelStore } from '~/features/whatsapp/stores/chatPanelStore';
import { type ChatDraftEntry, clearChatDraft, getChatDraft, setChatDraft } from '~/features/whatsapp/stores/draftStore';
import { resolveApplicantNameParts } from '~/features/whatsapp/utils/resolveApplicantNameParts';
import {
    resolveTemplatePickerOpenChange,
    TEMPLATE_PICKER_OPEN_EVENT_TYPES,
} from '~/features/whatsapp/utils/resolveTemplatePickerOpenChange';
import {
    namedVariablesOf,
    templateNeedsApplicant,
    templateNeedsPartner,
} from '~/features/whatsapp/utils/templateNeedsApplicant';
import useUserInfo from '~/hooks/useUserInfo';
import { cn } from '~/utils/cnTailwind';

type Props = {
    chatId: string;
    windowState: ChatWindowState;
};

const EMPTY_DRAFT: ChatDraftEntry = { content: '' };

const withValueAt = (values: Array<string> | undefined, index: number, value: string): Array<string> =>
    Array.from({ length: Math.max(values?.length ?? 0, index + 1) }, (_, i) =>
        i === index ? value : values?.[i] ?? '',
    );

const positionalSlotCount = (parameters: WhatsAppTemplate['parameters'] | undefined): number =>
    parameters?.kind === WHATSAPP_TEMPLATE_PARAMETER_KINDS.POSITIONAL ? parameters.count : 0;

const hasEmptySlot = (count: number, values: Array<string> | undefined): boolean =>
    Array.from({ length: count }, (_, i) => values?.[i]).some((value) => !value?.trim());

export const Composer = ({ chatId, windowState }: Props) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const previousChatIdRef = useRef(chatId);

    const isEmptyState = windowState === CHAT_WINDOW_STATES.NO_INBOUND_YET;
    const isExpired = windowState === CHAT_WINDOW_STATES.EXPIRED;
    const requiresTemplate = isEmptyState || isExpired;

    const defaultPickerTab = requiresTemplate
        ? WHATSAPP_TEMPLATE_PICKER_TABS.TEMPLATES
        : WHATSAPP_TEMPLATE_PICKER_TABS.QUICK_REPLIES;

    const [draft, setDraft] = useState<ChatDraftEntry>(() => getChatDraft(chatId) ?? EMPTY_DRAFT);
    const [manualOverlayOpen, setManualOverlayOpen] = useState(false);
    const [dismissedForcedOverlay, setDismissedForcedOverlay] = useState(false);

    const user = useUserInfo();
    const { data: userData } = useUserData(user?.id);
    const { data: templates = [], isError: templatesFailed, refetch: refetchTemplates } = useWhatsAppTemplates();
    const { data: chats } = useChats();
    const chat = chats?.find((entry) => entry.id === chatId);
    // Opened chat (not the pane's KN1/KN2 chatId) so lead context matches ChatView.
    const activeChatId = useChatPanelStore.use.activeChatId();
    const loanRequestId = useChatContextLoanRequestId(activeChatId);
    const { data: lead } = useLead(loanRequestId);
    const applicantNameParts = resolveApplicantNameParts(lead, chat?.phone);
    const partnerIds = chat?.partnerIds ?? [];
    const {
        data: partnerContacts,
        isError: partnerContactsFailed,
        isLoading: partnerContactsLoading,
    } = usePartnerContacts();
    // Preview only — own linked partner with phone = chat phone; backend re-derives on send.
    const partnerContact = partnerContacts?.find(
        (contact) => partnerIds.includes(contact.id) && contact.phoneE164 === chat?.phone,
    );
    // Preview gate only — admin: any partnerId; backend still requires effective (unlocked, phone-matching) link.
    const isAdmin = user?.role === USER_ROLES.admin;
    const hasPartner = isAdmin ? partnerIds.length > 0 : Boolean(partnerContact);
    const autoOpenTemplatePicker = useChatPanelStore.use.autoOpenTemplatePicker();

    const sendMessage = useSendMessageMutation(chatId);
    const sendTemplate = useSendTemplateMessageMutation(chatId);
    const uploadMedia = useUploadMediaMutation(chatId);

    useLayoutEffect(() => {
        if (!autoOpenTemplatePicker) return;
        setManualOverlayOpen(true);
        clearAutoOpenTemplatePicker();
    }, [autoOpenTemplatePicker]);

    // Only reset on an actual chatId change (switching chats) — must not fire on the
    // initial mount, which would otherwise stomp the autoOpenTemplatePicker layout effect.
    useEffect(() => {
        if (previousChatIdRef.current === chatId) return;
        previousChatIdRef.current = chatId;
        setDraft(getChatDraft(chatId) ?? EMPTY_DRAFT);
        setManualOverlayOpen(false);
        setDismissedForcedOverlay(false);
    }, [chatId]);

    const selectedTemplate = templates.find((template) => template.templateName === draft.templateName);
    const forcedOverlayOpen = requiresTemplate && !selectedTemplate && !dismissedForcedOverlay;
    const overlayOpen = forcedOverlayOpen || manualOverlayOpen;
    // Independent of overlayOpen — dismissing the picker must never unlock free text while a template is required.
    const freeTextLocked = requiresTemplate && !selectedTemplate;

    const persistDraft = (next: ChatDraftEntry) => {
        setDraft(next);
        if (next.templateName || next.content.trim()) {
            setChatDraft(chatId, next);
        } else {
            clearChatDraft(chatId);
        }
    };

    const handleTextChange = (value: string) => {
        persistDraft({ ...draft, content: value });
        setManualOverlayOpen((prev) =>
            resolveTemplatePickerOpenChange(prev, {
                hasContent: value.trim().length > 0,
                type: TEMPLATE_PICKER_OPEN_EVENT_TYPES.INPUT_CHANGE,
            }),
        );
    };

    const handleInputFocus = () => {
        setManualOverlayOpen((prev) =>
            resolveTemplatePickerOpenChange(prev, {
                inputEmpty: draft.content.trim().length === 0,
                type: TEMPLATE_PICKER_OPEN_EVENT_TYPES.INPUT_FOCUS,
            }),
        );
    };

    const closeOverlay = () => {
        setManualOverlayOpen(false);
        setDismissedForcedOverlay(true);
    };

    const handleIconClick = () => {
        if (overlayOpen) {
            closeOverlay();
            return;
        }
        setDismissedForcedOverlay(false);
        setManualOverlayOpen((prev) =>
            resolveTemplatePickerOpenChange(prev, { type: TEMPLATE_PICKER_OPEN_EVENT_TYPES.ICON_CLICK }),
        );
    };

    const handleUseTemplate = (template: WhatsAppTemplate) => {
        persistDraft({
            content: '',
            templateLanguage: template.languages[0],
            templateName: template.templateName,
        });
        setManualOverlayOpen(false);
    };

    const handleClearTemplate = () => {
        persistDraft(EMPTY_DRAFT);
        setDismissedForcedOverlay(false);
    };

    const handleNamedValueChange = (name: string, value: string) => {
        persistDraft({ ...draft, namedValues: { ...draft.namedValues, [name]: value } });
    };

    const handleHeaderNamedValueChange = (name: string, value: string) => {
        persistDraft({ ...draft, headerNamedValues: { ...draft.headerNamedValues, [name]: value } });
    };

    const handlePositionalValueChange = (index: number, value: string) => {
        persistDraft({ ...draft, positionalValues: withValueAt(draft.positionalValues, index, value) });
    };

    const handleHeaderPositionalValueChange = (index: number, value: string) => {
        persistDraft({ ...draft, headerPositionalValues: withValueAt(draft.headerPositionalValues, index, value) });
    };

    const previewValues: Record<WhatsAppVariableName, string | null | undefined> = {
        agent_email: user?.email,
        agent_first_name: user?.firstName,
        agent_last_name: user?.lastName,
        agent_phone: user?.telephone,
        agent_salutation: formatWhatsAppAgentSalutation(userData?.gender),
        agent_title: userData?.title,
        applicant_first_name: applicantNameParts?.firstName,
        applicant_last_name: applicantNameParts?.lastName,
        applicant_salutation: applicantNameParts?.salutation,
        partner_email: partnerContact?.email,
        partner_first_name: partnerContact?.firstName,
        partner_last_name: partnerContact?.lastName,
    };

    const selectedTemplateLanguage = draft.templateLanguage ?? selectedTemplate?.languages[0];
    const missingLoanRequest =
        templateNeedsApplicant({
            headerNamedValues: draft.headerNamedValues,
            language: selectedTemplateLanguage,
            namedValues: draft.namedValues,
            template: selectedTemplate,
        }) && !loanRequestId;
    const missingPartner =
        templateNeedsPartner({
            headerNamedValues: draft.headerNamedValues,
            namedValues: draft.namedValues,
            template: selectedTemplate,
        }) && !hasPartner;
    const positionalCount = positionalSlotCount(selectedTemplate?.parameters);
    const headerPositionalCount = positionalSlotCount(selectedTemplate?.headerParameters);

    const missingCustomIn = (
        variables: Record<string, TemplateVariableSource>,
        values: Record<string, string> | undefined,
    ) =>
        Object.entries(variables).some(
            ([name, variable]) =>
                variable.source === WHATSAPP_TEMPLATE_VARIABLE_SOURCES.CUSTOM && !values?.[name]?.trim(),
        );
    const missingVariable =
        missingCustomIn(namedVariablesOf(selectedTemplate?.parameters), draft.namedValues) ||
        missingCustomIn(namedVariablesOf(selectedTemplate?.headerParameters), draft.headerNamedValues) ||
        hasEmptySlot(positionalCount, draft.positionalValues) ||
        hasEmptySlot(headerPositionalCount, draft.headerPositionalValues);

    // divisionId is checked separately in handleSendTemplate (toasts CALLING_USER_HAS_NO_DIVISION)
    // rather than folded in here, so the guard stays reachable/testable via the Send button.
    const canSendTemplate =
        Boolean(selectedTemplate?.sendable) &&
        !missingLoanRequest &&
        !missingPartner &&
        !missingVariable &&
        !sendTemplate.isPending;

    const canSendText =
        !requiresTemplate && !selectedTemplate && draft.content.trim().length > 0 && !sendMessage.isPending;

    // Non-admin hasPartner depends on the partnerContacts query — reflect its error/loading state in the hint.
    let partnerHint: string | undefined = t('WHATSAPP_CHAT.TEMPLATE_MODAL.MISSING_PARTNER');
    if (!isAdmin && partnerContactsFailed) {
        partnerHint = t('WHATSAPP_CHAT.ERROR.CONTACTS_LOAD_ERROR');
    } else if (!isAdmin && partnerContactsLoading) {
        partnerHint = undefined;
    }

    let sendDisabledReason: string | undefined;
    if (selectedTemplate && missingLoanRequest) {
        sendDisabledReason = t('WHATSAPP_CHAT.TEMPLATE_MODAL.MISSING_LOAN_REQUEST');
    } else if (selectedTemplate && missingPartner) {
        sendDisabledReason = partnerHint;
    }

    const handleSendTemplate = () => {
        if (!selectedTemplate) return;
        if (!canSendTemplate) return;

        const templateLanguage = draft.templateLanguage ?? selectedTemplate.languages[0];
        if (!templateLanguage) return;

        if (!user?.divisionId) {
            toast.error(t('WHATSAPP_CHAT.ERROR.CALLING_USER_HAS_NO_DIVISION'));
            return;
        }
        const filterFilled = (values: Record<string, string> | undefined) =>
            Object.fromEntries(Object.entries(values ?? {}).filter(([, value]) => value.trim().length > 0));
        const filledNamedValues = filterFilled(draft.namedValues);
        const filledHeaderNamedValues = filterFilled(draft.headerNamedValues);

        sendTemplate.mutate(
            {
                customTemplateValues: Object.keys(filledNamedValues).length > 0 ? filledNamedValues : undefined,
                divisionId: user.divisionId,
                headerCustomTemplateValues:
                    Object.keys(filledHeaderNamedValues).length > 0 ? filledHeaderNamedValues : undefined,
                loanRequestId,
                templateHeaderParameters:
                    headerPositionalCount > 0
                        ? Array.from(
                              { length: headerPositionalCount },
                              (_, i) => draft.headerPositionalValues?.[i] ?? '',
                          )
                        : undefined,
                templateLanguage,
                templateName: selectedTemplate.templateName,
                templateParameters:
                    positionalCount > 0
                        ? Array.from({ length: positionalCount }, (_, i) => draft.positionalValues?.[i] ?? '')
                        : undefined,
            },
            {
                onSuccess: () => {
                    toast.success(t('WHATSAPP_CHAT.TEMPLATE_MODAL.SEND_SUCCESS'));
                    persistDraft(EMPTY_DRAFT);
                },
            },
        );
    };

    const handleSendText = () => {
        const trimmed = draft.content.trim();
        if (requiresTemplate || !trimmed || sendMessage.isPending) return;
        sendMessage.mutate(
            { messageType: CHAT_MESSAGE_TYPES.TEXT, text: trimmed },
            {
                onSuccess: () => {
                    persistDraft(EMPTY_DRAFT);
                },
            },
        );
    };

    const handleSend = () => {
        if (selectedTemplate) {
            handleSendTemplate();
            return;
        }
        handleSendText();
    };

    const handleFileSelected = (file: File | undefined) => {
        if (!file || requiresTemplate) return;

        const mediaType = deriveMediaTypeFromMimeType(file.type);
        const limit = CHAT_MEDIA_SIZE_LIMITS[mediaType];
        if (file.size > limit) {
            toast.error(t('WHATSAPP_CHAT.ERROR.FILE_TOO_LARGE', { limitMb: Math.round(limit / 1024 / 1024) }));
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        const originalFileName = file.name;
        uploadMedia.mutate(file, {
            onSuccess: (attachment) => {
                sendMessage.mutate({
                    mediaFileName: originalFileName,
                    mediaKey: attachment.key,
                    messageType: CHAT_MESSAGE_TYPES.MEDIA,
                    mimeType: attachment.mimeType,
                });
            },
        });
    };

    const canSend = selectedTemplate ? canSendTemplate : canSendText;
    const composerPlaceholder = t(
        isExpired ? 'WHATSAPP_CHAT.COMPOSER.EXPIRED_PLACEHOLDER' : 'WHATSAPP_CHAT.COMPOSER.PLACEHOLDER',
    );

    return (
        <div className="shrink-0 border-t border-outline-light px-4 py-3">
            {isEmptyState && (
                <>
                    <div className="mb-2 flex items-center gap-2 text-sm text-midnight">
                        <LuMessageCircle className="shrink-0" />
                        <span className="font-semibold">{t('WHATSAPP_CHAT.COMPOSER.EMPTY_STATE_TITLE')}</span>
                    </div>
                    <p className="mb-2 text-sm text-grey">{t('WHATSAPP_CHAT.COMPOSER.EMPTY_STATE_EXPLANATION')}</p>
                </>
            )}
            <div className="flex flex-col gap-2">
                {overlayOpen && (
                    <TemplatePickerOverlay
                        templates={templates}
                        previewValues={previewValues}
                        hasLoanRequest={Boolean(loanRequestId)}
                        hasPartner={hasPartner}
                        defaultTab={defaultPickerTab}
                        onUseTemplate={handleUseTemplate}
                        onClose={closeOverlay}
                        hasLoadError={templatesFailed}
                        onRetry={() => {
                            refetchTemplates();
                        }}
                    />
                )}
                <div className="flex items-end gap-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        data-testid="chat-file-input"
                        onChange={(e) => handleFileSelected(e.target.files?.[0])}
                    />
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                aria-label={t('WHATSAPP_CHAT.COMPOSER.ATTACH')}
                                className="shrink-0 p-2 text-midnight disabled:opacity-50"
                                disabled={requiresTemplate || uploadMedia.isPending || Boolean(selectedTemplate)}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <LuPaperclip />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent hideArrow side="top" variant={TooltipVariant.LIGHT}>
                            {t('WHATSAPP_CHAT.COMPOSER.ATTACH')}
                        </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                aria-label={t('WHATSAPP_CHAT.COMPOSER.CHOOSE_TEMPLATE')}
                                aria-pressed={overlayOpen}
                                className={cn(
                                    'shrink-0 rounded p-2 outline outline-1 outline-offset-[-1px]',
                                    overlayOpen
                                        ? 'bg-surface-light text-midnight outline-midnight'
                                        : 'text-grey outline-transparent',
                                )}
                                onClick={handleIconClick}
                            >
                                <LuMessageSquareText />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent hideArrow side="top" variant={TooltipVariant.LIGHT}>
                            {t('WHATSAPP_CHAT.COMPOSER.CHOOSE_TEMPLATE')}
                        </TooltipContent>
                    </Tooltip>
                    <ChipComposerInput
                        template={selectedTemplate}
                        templateLanguage={draft.templateLanguage}
                        namedValues={draft.namedValues ?? {}}
                        headerNamedValues={draft.headerNamedValues ?? {}}
                        positionalValues={draft.positionalValues ?? []}
                        headerPositionalValues={draft.headerPositionalValues ?? []}
                        onNamedValueChange={handleNamedValueChange}
                        onHeaderNamedValueChange={handleHeaderNamedValueChange}
                        onPositionalValueChange={handlePositionalValueChange}
                        onHeaderPositionalValueChange={handleHeaderPositionalValueChange}
                        onClearTemplate={handleClearTemplate}
                        previewValues={previewValues}
                        text={draft.content}
                        onTextChange={handleTextChange}
                        onFocus={handleInputFocus}
                        onEnterSend={handleSend}
                        disabled={freeTextLocked}
                        placeholder={composerPlaceholder}
                    />
                    <button
                        type="button"
                        aria-label={t('WHATSAPP_CHAT.COMPOSER.SEND')}
                        className="shrink-0 rounded-full bg-midnight p-2 text-white disabled:opacity-50"
                        disabled={!canSend}
                        title={sendDisabledReason}
                        onClick={handleSend}
                    >
                        <LuSend />
                    </button>
                </div>
            </div>
        </div>
    );
};
